import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Plus, Tag } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import HomepageEditor from "@/components/HomepageEditor";
import { imgSrc } from "@/lib/img";

const empty = { name: "", description: "", price: 0, category: "kurti", images: [], sizes: ["S","M","L","XL"], colors: [""], stock: 50, featured: false };
const emptyCoupon = { code: "", type: "percent", value: 10, min_subtotal: 0, max_uses: "", active: true };

export default function Admin() {
  const { user, API, authHeader, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [couponForm, setCouponForm] = useState(emptyCoupon);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { nav("/login?next=/admin"); return; }
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const refresh = async () => {
    const [s, p, o, c] = await Promise.all([
      axios.get(`${API}/admin/stats`, { headers: authHeader() }),
      axios.get(`${API}/products`),
      axios.get(`${API}/orders`, { headers: authHeader() }),
      axios.get(`${API}/coupons`, { headers: authHeader() }),
    ]);
    setStats(s.data); setProducts(p.data); setOrders(o.data); setCoupons(c.data);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price),
      stock: parseInt(form.stock || 0),
      images: form.images.filter(x => x.trim()),
      colors: form.colors.filter(x => x.trim()),
    };
    try {
      if (editingId) {
        await axios.put(`${API}/products/${editingId}`, payload, { headers: authHeader() });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, payload, { headers: authHeader() });
        toast.success("Product created");
      }
      setForm(empty); setEditingId(null); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await axios.delete(`${API}/products/${id}`, { headers: authHeader() });
    toast.success("Deleted"); refresh();
  };

  const edit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, description: p.description, price: p.price,
      category: p.category, images: p.images.length ? p.images : [],
      sizes: p.sizes, colors: p.colors.length ? p.colors : [""],
      stock: p.stock, featured: p.featured,
    });
    setTab("products"); window.scrollTo(0, 0);
  };

  const setOrderStatus = async (oid, status) => {
    await axios.put(`${API}/orders/${oid}/status`, { status }, { headers: authHeader() });
    toast.success("Order updated"); refresh();
  };

  const saveCoupon = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...couponForm,
        code: couponForm.code.trim().toUpperCase(),
        value: parseFloat(couponForm.value),
        min_subtotal: parseFloat(couponForm.min_subtotal) || 0,
        max_uses: couponForm.max_uses === "" ? null : parseInt(couponForm.max_uses),
      };
      await axios.post(`${API}/coupons`, payload, { headers: authHeader() });
      toast.success("Coupon created");
      setCouponForm(emptyCoupon);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const delCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await axios.delete(`${API}/coupons/${id}`, { headers: authHeader() });
    toast.success("Deleted"); refresh();
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="label-eyebrow">Atelier</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3 mb-10">Admin Dashboard</h1>

      <div className="flex gap-2 border-b border-[#E5DFD3] mb-10 overflow-x-auto">
        {["overview","products","orders","coupons","homepage"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-5 py-3 text-xs uppercase tracking-[0.25em] whitespace-nowrap ${tab === t ? "border-b-2 border-[#6B1A24] text-[#6B1A24]" : "text-[#5C5454]"}`}>{t}</button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            ["Products", stats.products],
            ["Orders", stats.orders],
            ["Paid", stats.paid_orders],
            ["Customers", stats.users],
            ["Revenue", `$${stats.revenue.toFixed(2)}`],
          ].map(([l, v]) => (
            <div key={l} className="border border-[#E5DFD3] p-6">
              <div className="label-eyebrow">{l}</div>
              <div className="font-display text-3xl mt-3 tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "products" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <form onSubmit={save} className="space-y-4 lg:col-span-1 border border-[#E5DFD3] p-6">
            <h2 className="font-display text-2xl">{editingId ? "Edit" : "New"} product</h2>
            <input data-testid="admin-prod-name" required placeholder="Name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2 focus:outline-none focus:border-[#6B1A24]" />
            <textarea data-testid="admin-prod-desc" required placeholder="Description" rows={3} value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="w-full bg-transparent border border-[#E5DFD3] p-2 focus:outline-none focus:border-[#6B1A24]" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="admin-prod-price" required type="number" step="0.01" placeholder="Price" value={form.price} onChange={e=>setForm({...form, price:e.target.value})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2" />
              <input data-testid="admin-prod-stock" type="number" placeholder="Stock" value={form.stock} onChange={e=>setForm({...form, stock:e.target.value})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2" />
            </div>
            <select data-testid="admin-prod-category" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2">
              <option value="kurti">Kurti</option>
              <option value="co-ord">Co-ord Set</option>
            </select>
            <div>
              <div className="label-eyebrow mb-2">Product images</div>
              <ImageUploader value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} max={6} />
            </div>
            <input data-testid="admin-prod-colors" placeholder="Colors (comma-sep)" value={form.colors.join(", ")} onChange={e=>setForm({...form, colors:e.target.value.split(",").map(x=>x.trim())})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2" />
            <label className="flex items-center gap-2 text-sm"><input data-testid="admin-prod-featured" type="checkbox" checked={form.featured} onChange={e=>setForm({...form, featured:e.target.checked})} /> Featured</label>
            <div className="flex gap-2">
              <button data-testid="admin-prod-save" className="flex-1 bg-[#6B1A24] text-[#FAFAF7] py-3 uppercase text-xs tracking-[0.25em]">{editingId ? "Update" : "Create"}</button>
              {editingId && <button type="button" onClick={()=>{setForm(empty); setEditingId(null);}} className="px-4 border border-[#2D2626] uppercase text-xs tracking-[0.25em]">Cancel</button>}
            </div>
          </form>

          <div className="lg:col-span-2 space-y-3">
            {products.map(p => (
              <div key={p.id} className="border border-[#E5DFD3] p-4 flex items-center gap-4" data-testid={`admin-product-${p.id}`}>
                <div className="w-16 h-20 bg-[#F3EFEA]">{p.images[0] && <img src={imgSrc(p.images[0])} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1">
                  <div className="font-display text-lg">{p.name}</div>
                  <div className="text-xs text-[#5C5454]">{p.category} · ${p.price.toFixed(2)} · Stock {p.stock}{p.featured ? " · Featured" : ""}</div>
                </div>
                <button onClick={()=>edit(p)} data-testid={`admin-edit-${p.id}`} className="text-xs uppercase tracking-[0.2em] text-[#6B1A24]">Edit</button>
                <button onClick={()=>del(p.id)} data-testid={`admin-del-${p.id}`} className="text-[#9E2A2B] p-2"><Trash2 size={16} strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3">
          {orders.length === 0 && <p className="text-sm text-[#5C5454]">No orders yet.</p>}
          {orders.map(o => (
            <div key={o.id} className="border border-[#E5DFD3] p-5" data-testid={`admin-order-${o.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <div className="font-mono text-sm">{o.id.slice(0,8).toUpperCase()}</div>
                  <div className="text-xs text-[#5C5454]">{o.user_email} · {new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="font-display text-xl tabular-nums">${o.total.toFixed(2)}</div>
                <select data-testid={`admin-order-status-${o.id}`} value={o.status} onChange={e=>setOrderStatus(o.id, e.target.value)} className="bg-transparent border-b border-[#2D2626] py-1 text-sm">
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="fulfilled">Fulfilled</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="mt-3 text-xs text-[#5C5454]">
                {o.items.length} item(s) · ship to {o.shipping?.city || "—"}, {o.shipping?.country || "—"}
                {o.coupon_code && <span className="ml-2 text-[#6B1A24]">· coupon {o.coupon_code} (−${(o.discount || 0).toFixed(2)})</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "coupons" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <form onSubmit={saveCoupon} className="space-y-4 lg:col-span-1 border border-[#E5DFD3] p-6">
            <h2 className="font-display text-2xl">New coupon</h2>
            <input
              data-testid="admin-coupon-code"
              required
              placeholder="CODE (e.g., FESTIVE15)"
              value={couponForm.code}
              onChange={e=>setCouponForm({...couponForm, code:e.target.value.toUpperCase()})}
              className="w-full bg-transparent border-b border-[#E5DFD3] py-2 font-mono uppercase tracking-wider focus:outline-none focus:border-[#6B1A24]"
            />
            <select data-testid="admin-coupon-type" value={couponForm.type} onChange={e=>setCouponForm({...couponForm, type:e.target.value})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2">
              <option value="percent">% off subtotal</option>
              <option value="fixed">Fixed $ off</option>
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="admin-coupon-value" required type="number" step="0.01" placeholder={couponForm.type === "percent" ? "% (1-100)" : "$ amount"} value={couponForm.value} onChange={e=>setCouponForm({...couponForm, value:e.target.value})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2" />
              <input data-testid="admin-coupon-min" type="number" step="0.01" placeholder="Min subtotal $" value={couponForm.min_subtotal} onChange={e=>setCouponForm({...couponForm, min_subtotal:e.target.value})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2" />
            </div>
            <input data-testid="admin-coupon-max-uses" type="number" placeholder="Max uses (blank = unlimited)" value={couponForm.max_uses} onChange={e=>setCouponForm({...couponForm, max_uses:e.target.value})} className="w-full bg-transparent border-b border-[#E5DFD3] py-2" />
            <label className="flex items-center gap-2 text-sm"><input data-testid="admin-coupon-active" type="checkbox" checked={couponForm.active} onChange={e=>setCouponForm({...couponForm, active:e.target.checked})} /> Active</label>
            <button data-testid="admin-coupon-save" className="w-full bg-[#6B1A24] text-[#FAFAF7] py-3 uppercase text-xs tracking-[0.25em]">Create coupon</button>
          </form>

          <div className="lg:col-span-2 space-y-3">
            {coupons.length === 0 && <p className="text-sm text-[#5C5454]">No coupons yet.</p>}
            {coupons.map(c => (
              <div key={c.id} className="border border-[#E5DFD3] p-4 flex items-center gap-4" data-testid={`admin-coupon-${c.code}`}>
                <Tag size={20} strokeWidth={1.5} className="text-[#6B1A24]" />
                <div className="flex-1">
                  <div className="font-mono text-base tracking-wider">{c.code}</div>
                  <div className="text-xs text-[#5C5454]">
                    {c.type === "percent" ? `${c.value}% off` : `$${c.value.toFixed(2)} off`}
                    {c.min_subtotal > 0 && ` · min $${c.min_subtotal.toFixed(2)}`}
                    {` · used ${c.uses}${c.max_uses ? "/" + c.max_uses : ""}`}
                    {!c.active && " · inactive"}
                  </div>
                </div>
                <button onClick={()=>delCoupon(c.id)} data-testid={`admin-coupon-del-${c.code}`} className="text-[#9E2A2B] p-2"><Trash2 size={16} strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === "homepage" && <HomepageEditor />}
    </div>
  );
}
